import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import profiles from './fixed-window-profiles.json';

export function profileShapes(component) {
  return component.shapes.map(item => {
    const shape = new THREE.Shape(item.outer.map(([x,y]) => new THREE.Vector2(x/1000,y/1000)));
    item.holes.forEach(ring => shape.holes.push(new THREE.Path(ring.map(([x,y]) => new THREE.Vector2(x/1000,y/1000)))));
    return shape;
  });
}

export function member(profile, side, width, height, cornerSide, profileDepth) {
  const geometry = profile.isBufferGeometry ? profile.clone() : new THREE.ExtrudeGeometry(profile, {depth:1,steps:1,bevelEnabled:false,curveSegments:3});
  const points = geometry.attributes.position;
  for (let i=0;i<points.count;i++) {
    const depth=points.getX(i), inset=points.getY(i), t=points.getZ(i);
    if (side==='sill') points.setXYZ(i,(t-.5)*(width-2*inset),inset,depth);
    if (side==='head') points.setXYZ(i,(t-.5)*(width-2*inset),height-inset,depth);
    if (side==='left') points.setXYZ(i,-width/2+inset,inset+t*(height-2*inset),depth);
    if (side==='right') points.setXYZ(i,width/2-inset,inset+t*(height-2*inset),depth);
    if (cornerSide && (side==='sill' || side==='head')) {
      const left=-width/2+inset, right=width/2+(cornerSide==='a' ? -1 : 1)*(depth-profileDepth/2);
      points.setX(i,left+t*(right-left));
    }
  }
  // The depth→Z permutation reverses sill/right faces; restore outward winding.
  if (side==='sill' || side==='right') for(let i=0;i<points.count;i+=3) {
    const a=[points.getX(i+1),points.getY(i+1),points.getZ(i+1)];
    points.setXYZ(i+1,points.getX(i+2),points.getY(i+2),points.getZ(i+2));
    points.setXYZ(i+2,...a);
  }
  geometry.computeVertexNormals();
  return geometry;
}

export function makeFixedWindow(product, materials, options={}) {
  const data=profiles[product.id], group=new THREE.Group(), frame=materials.makeFrame();
  const materialByRole={frame,thermal:materials.thermal,gasket:materials.gasket};
  const grouped={frame:[],thermal:[],gasket:[]};
  const sides=options.section ? [options.section] : options.cornerSide ? ['sill','head','left'] : ['sill','head','left','right'];
  const width=options.section ? .38 : product.width, height=options.section ? .2 : product.height;
  for (const component of data.components) {
    const shapes=profileShapes(component);
    for (const side of sides) grouped[component.role].push(member(shapes,side,width,height,options.cornerSide,data.depth_mm/1000));
  }
  for (const [role, geometries] of Object.entries(grouped)) {
    if (!geometries.length) continue;
    const geometry=mergeGeometries(geometries,false); geometries.forEach(g=>g.dispose());
    const mesh=new THREE.Mesh(geometry, materialByRole[role]); mesh.position.z=-data.depth_mm/2000;
    mesh.castShadow=true; mesh.receiveShadow=true; mesh.userData.profileRole=role;
    if (role==='frame') mesh.material.side=THREE.DoubleSide;
    group.add(mesh);
  }
  if (!options.section) {
    const inset=Math.max(.035,data.height_mm/1000-.012);
    const glassGeometry=new THREE.BoxGeometry(width-2*inset,height-2*inset,.028);
    if (options.cornerSide) {
      const points=glassGeometry.attributes.position;
      for(let i=0;i<points.count;i++) if(points.getX(i)>0) {
        // Opposed 45° glass ends leave a narrow seam, never a vertical frame.
        points.setX(i,width/2+(options.cornerSide==='a' ? -1 : 1)*points.getZ(i)-.001);
      }
      glassGeometry.computeVertexNormals();
    }
    const glass=new THREE.Mesh(glassGeometry,materials.glass);
    glass.name='fixed-window-glass';
    glass.position.set(0,height/2,0); glass.receiveShadow=true; group.add(glass);
  }
  group.userData.fixed=true;
  group.userData.frameSides=sides;
  group.userData.cornerSide=options.cornerSide || null;
  group.userData.sourceProfileDepth=data.depth_mm;
  group.userData.panelPositions=[];
  group.userData.animate=()=>{};
  group.userData.pickTargets=group.children;
  group.traverse(o=>{ if(o.isMesh) o.userData.productId=product.id; });
  return {group,frame,animate:()=>{},progress:0,raised:0,viewHeightOffset:options.section ? 0 : .64};
}

export function makeFixedSpecimen(product,type,frame,materials) {
  const model=makeFixedWindow(product,{...materials,makeFrame:()=>frame},{section:type});
  model.group.userData.profileCode=product.name;
  model.group.userData.trackCount=0;
  model.group.userData.profileDepth=profiles[product.id].depth_mm/1000;
  return model.group;
}